/**
 * ModuleRegistrationRegistry — "is this module still registering?"
 *
 * WHY THIS EXISTS
 * ---------------
 * The shell resolves a route's component out of the ViewRegistry. A module's
 * views land there in two passes: the gateway manifest (Pass 1) and the module
 * bundle's own manifest (Pass 2). Whenever the gateway manifest carries no view
 * descriptors — which is the normal case for the hermetic mock stack, and the
 * transient case for any real deployment while Pass 2 fans out across ~60
 * modules — a deep link opened during boot resolves NOTHING for a while.
 *
 * A missing resolution has two completely different meanings, and the shell used
 * to render the same settled negative verdict for both:
 *
 *   "this module has not finished registering yet"  — transient, self-resolving
 *   "this module registered, and exports no such view" — a settled fact
 *
 * Presenting the first as the second told the operator "This page isn't wired up
 * to a federated component yet" about a page that was seconds away from
 * rendering. This registry is the missing distinction: App.tsx marks each module
 * pending before Pass 2 dispatches it and settles it when that module's
 * registration finishes (successfully or not), and ModuleView renders a
 * REGISTERING state — not the error state — for as long as the module in the URL
 * is still pending.
 *
 * Deliberately independent of `remoteHealth`: that registry records the OUTCOME
 * of a remote load (ok / empty / unreachable) and has no entry at all for a load
 * that has not finished, so it cannot answer "in flight" without conflating it
 * with "never attempted".
 */

type Listener = () => void;

class ModuleRegistrationRegistry {
  private pending = new Set<string>();
  private listeners = new Set<Listener>();

  /** Mark a module as having a registration in flight. */
  begin(moduleId: string | undefined | null): void {
    if (!moduleId || this.pending.has(moduleId)) return;
    this.pending.add(moduleId);
    this.emit();
  }

  /** Mark a module's registration attempt as finished — success OR failure. */
  settle(moduleId: string | undefined | null): void {
    if (!moduleId) return;
    if (this.pending.delete(moduleId)) this.emit();
  }

  /**
   * True while the given module's registration is in flight. With no argument,
   * true while ANY module is still registering.
   */
  isPending(moduleId?: string | null): boolean {
    if (moduleId === undefined || moduleId === null) return this.pending.size > 0;
    return this.pending.has(moduleId);
  }

  /** Number of modules still registering. Exposed for diagnostics/tests. */
  pendingCount(): number {
    return this.pending.size;
  }

  /** Drop all in-flight state (manifest reload, tenant switch). */
  reset(): void {
    if (this.pending.size === 0) return;
    this.pending.clear();
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export const moduleRegistration = new ModuleRegistrationRegistry();
